import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ArtistPortalSyncStatus, isArtistPortalEarlyAccessService } from 'fym-shared/src/smartlinks/artistPortals';
import {
  fymAPI,
  useGetServiceConnectionsQuery,
  usePatchServiceConnectionMutation,
  useRequestEarlyAccessMutation,
  useRequestServiceConnectionMutation,
} from '../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { ServiceConnection, ServiceConnectionRequestReason } from '../../../redux/api/fymAPI.types';
import { StoreType } from '../../../redux/store';
import { useRealtimePollInterval } from '../../realtime/useRealtimePollInterval';

interface UseServiceConnectionsProps {
  accountId: number;
}

/** How often the strip asks again while a sync is on the queue or running. */
const SYNC_POLL_MS = 5000;

/** True while at least one service is waiting on, or inside, a sync. */
function hasRunningSync(connections: ServiceConnection[] | undefined): boolean {
  return (connections ?? []).some(
    (connection) =>
      connection.syncStatus === ArtistPortalSyncStatus.queued ||
      connection.syncStatus === ArtistPortalSyncStatus.running
  );
}

/**
 * The connection cards (music services, then distributors) and the things an
 * artist can do to one.
 *
 * The read always answers with every card, the untouched ones synthesized, so the
 * strip never has an empty state of its own. Every row also carries the
 * artist-portal fields (`authKind`, `connectAvailable`, `artist`, `syncStatus`,
 * `syncedThrough`, `lastSyncAt`, `lastSyncError`); the slice fills them with
 * safe defaults when an older server sends none. The five portal writes live in
 * `useArtistPortalConnect`, because they need a sign-in surface this hook does
 * not own.
 *
 * A queued or running sync turns the read into a poll, and the moment it
 * finishes the Streaming numbers are dropped — otherwise "Sync now" would say it
 * is done while the screen still showed yesterday. The `artistPortal.synced` event
 * reads the cards again, so the poll runs only while the realtime socket is down.
 */
export function useServiceConnections({ accountId }: UseServiceConnectionsProps) {
  const dispatch = useDispatch<StoreType['dispatch']>();
  const [syncPollMs, setSyncPollMs] = useState(0);
  const pollingInterval = useRealtimePollInterval(syncPollMs);

  const { data, isLoading, error } = useGetServiceConnectionsQuery({ accountId }, { pollingInterval });
  const [patchServiceConnection, { isLoading: isSaving, error: saveError, reset: resetSave }] =
    usePatchServiceConnectionMutation();
  const [requestServiceConnection, { isLoading: isRequesting, error: requestError, reset: resetRequest }] =
    useRequestServiceConnectionMutation();
  const [requestEarlyAccess] = useRequestEarlyAccessMutation();

  const isSyncing = useMemo(() => hasRunningSync(data?.connections), [data]);
  const wasSyncingRef = useRef(false);

  useEffect(() => {
    setSyncPollMs(isSyncing ? SYNC_POLL_MS : 0);
    if (wasSyncingRef.current && !isSyncing) {
      dispatch(fymAPI.util.invalidateTags(['ArtistPortalOverview', 'ArtistPortalTracks', 'Streaming']));
    }
    wasSyncingRef.current = isSyncing;
  }, [dispatch, isSyncing]);

  /**
   * Reads the cards again. A child effect (the SoundCloud return banner) calls this before the
   * hook's own subscription exists, and the hook's `refetch` throws then (MUSIC24-WEB-B) — also
   * when another screen already filled the cache, so `isUninitialized` is no guard. A forced
   * read of the endpoint needs no subscription of this component.
   */
  const retry = useCallback(() => {
    const read = dispatch(
      fymAPI.endpoints.getServiceConnections.initiate({ accountId }, { forceRefetch: true, subscribe: false })
    );
    read.unwrap().catch(console.error);
  }, [accountId, dispatch]);

  /** Drops the last write error, so a sheet opened for another card starts clean. */
  const resetWriteError = useCallback(() => {
    resetSave();
    resetRequest();
  }, [resetRequest, resetSave]);

  const saveProfileUrl = useCallback(
    async (integrationType: string, artistProfileUrl: string | null): Promise<boolean> => {
      const result = await patchServiceConnection({ accountId, integrationType, artistProfileUrl });
      return isSuccessfulResult(result);
    },
    [accountId, patchServiceConnection]
  );

  /**
   * The old stub request. A built service still climbs the stub ladder (in
   * development only); an early-access service joins the waiting list instead,
   * with no login email.
   */
  const requestConnection = useCallback(
    async (integrationType: string, reason?: ServiceConnectionRequestReason): Promise<boolean> => {
      const result = isArtistPortalEarlyAccessService(integrationType)
        ? await requestEarlyAccess({ accountId, integrationType })
        : await requestServiceConnection({ accountId, integrationType, reason });
      return isSuccessfulResult(result);
    },
    [accountId, requestEarlyAccess, requestServiceConnection]
  );

  return {
    connections: data?.connections,
    isLoading,
    error,
    retry,
    saveProfileUrl,
    requestConnection,
    isSyncing,
    isWriting: isSaving || isRequesting,
    writeError: saveError ?? requestError,
    resetWriteError,
  };
}
